import { useParams } from "@solidjs/router";
import {
  CreatePublicAssetRequestConfig,
  GetAssetsRequestConfig,
  GetPublicAssetByBlockIdRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import { type ChatMessages, type RichText, useExportClient } from "@storytell/cp-client";
import { ExportFormat } from "@storytell/cp-client/src/gen/export/v3/export_service_pb";
import { dispatchGlobalEvent } from "@storytell/events";
import { SolidStreamingMarkdown } from "@storytell/markdown";
import { RichTextEditor, type RichTextEditorInstance, proseMirrorToRichText } from "@storytell/rich-text";
import type { StreamingBlockId } from "@storytell/streaming";
import type { AssetId } from "@storytell/types";
import type { PmDoc } from "@storytell/types/richText";
import { StButton } from "@storytell/ui/buttons";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { clsx } from "clsx";
import {
  TbAlertCircle,
  TbArrowBackUp,
  TbCheck,
  TbCode,
  TbCopy,
  TbDeviceFloppy,
  TbDownload,
  TbInfoCircle,
  TbLoader2,
  TbMarkdown,
  TbMinus,
  TbPaperclip,
  TbPencil,
  TbShare,
  TbTrash,
  TbX,
} from "solid-icons/tb";
import {
  type Component,
  Show,
  Suspense,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  onMount,
} from "solid-js";
import { createResource } from "solid-js";
import { isServer } from "solid-js/web";
import { ExtendedPaneMode, useAppUI } from "~/components/layouts/minimal/AppUIContext";
import { ArtifactSaveError, type SaveArtifactResponse, getBackendURL, saveArtifactToKB } from "~/lib/api/assets";
import { type JSONContent, clearArtifactEdit, loadArtifactEdit, saveArtifactEdit } from "~/stores/ArtifactPersistence";
import type { ArtifactState } from "~/stores/ArtifactStore";
import type { KBSearchResultsCollection, WebSearchResultsCollection } from "~/utils/citationResolver";
import { buildKBCitationIndex } from "~/utils/citationResolver";
import { copyMarkdownToClipboard, copyRichTextToClipboard } from "~/utils/copyRichText";
import { proseMirrorToMarkdown } from "~/utils/proseMirrorToMarkdown";
import { findTitle } from "~/utils/richTextUtils";
import { buildWebSearchCollection } from "~/utils/webSearchCollectionBuilder";
import { useWire } from "~/wire";
import { UnifiedCitationRenderer } from "./UnifiedCitationRenderer";
import { TextSelectionHandler } from "./messages/TextSelectionHandler";
import { createMarkdownStreamerFromDeltas } from "./messages/r/internal/markdownStreamerFromDeltas";

const toArrayBuffer = (bytes: Uint8Array<ArrayBufferLike>): ArrayBuffer => {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
};

function extractTextFromJSONContent(content: JSONContent): string {
  if (!content) return "";

  if (content.type === "text") {
    return content.text || "";
  }

  if (content.content) {
    return content.content.map((child) => extractTextFromJSONContent(child)).join(" ");
  }

  return "";
}

interface ArtifactViewerProps {
  artifact: ArtifactState;
  isActive: boolean;
  class?: string;
  editMode?: boolean; // Whether to open the artifact in edit mode
  mount?: Node; // Portal mount point for tooltips
}

export const ArtifactViewer: Component<ArtifactViewerProps> = (props) => {
  const wire = useWire();
  const { actions } = useAppUI();
  const params = useParams<{ chatId?: string }>();
  const client = useStorytellClient();

  const [editMode, setEditMode] = createSignal(props.editMode ?? false);
  const [hasLocalEdits, setHasLocalEdits] = createSignal(false);
  const [isLoadingEdit, setIsLoadingEdit] = createSignal(false);
  const [loadedEditContent, setLoadedEditContent] = createSignal<JSONContent | null>(null);
  const [originalContent, setOriginalContent] = createSignal<JSONContent | null>(null);
  const [isSavingToKB, setIsSavingToKB] = createSignal(false);
  const [saveSuccess, setSaveSuccess] = createSignal(false);
  const [panelWidth, setPanelWidth] = createSignal(1000); // Track panel width for responsive buttons
  const [hasDirtyChanges, setHasDirtyChanges] = createSignal(false); // Track unsaved changes in current session
  const [editorState, setEditorState] = createSignal({
    bold: false,
    italic: false,
    strike: false,
    code: false,
    codeBlock: false,
    bulletList: false,
    orderedList: false,
    heading1: false,
    heading2: false,
    heading3: false,
    paragraph: false,
  });

  let suppressDirtyTracking = false;

  // Fetch existing public asset for this block
  const [existingPublicAsset] = createResource(
    () => props.artifact.blockId,
    async (blockId) => {
      if (!blockId) return null;
      const request = GetPublicAssetByBlockIdRequestConfig.withBearerToken(wire.auth.token());
      const response = await client.fetch(request, { blockId }, undefined);
      return response.isOk() ? response.value : null;
    },
  );

  // Only render content when active for performance
  const shouldRenderContent = createMemo(() => props.isActive);

  // Ref for capturing rendered content for rich text copy
  const [contentRef, setContentRef] = createSignal<HTMLDivElement>();

  // Extract text from edited content for display in view mode
  const editedContentAsText = createMemo(() => {
    const content = loadedEditContent();
    return content ? extractTextFromJSONContent(content) : null;
  });

  const hasActualEdits = createMemo(() => {
    const loaded = loadedEditContent();
    if (!loaded) return false;

    let original = originalContent();
    if (!original && props.artifact.metadata?.richText) {
      original = JSON.parse(JSON.stringify(props.artifact.metadata.richText)) as JSONContent;
    }
    if (!original) return true;

    return JSON.stringify(loaded) !== JSON.stringify(original);
  });

  let saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  const debouncedSaveToIndexedDB = (content: JSONContent) => {
    if (saveDebounceTimer) {
      clearTimeout(saveDebounceTimer);
    }

    saveDebounceTimer = setTimeout(async () => {
      try {
        let original = originalContent();
        if (!original && props.artifact.metadata?.richText) {
          original = JSON.parse(JSON.stringify(props.artifact.metadata.richText)) as JSONContent;
          setOriginalContent(original);
        }
        await saveArtifactEdit(props.artifact.blockId, content, original, props.artifact.title, props.artifact.type);
        setHasLocalEdits(true);
      } catch (error) {
        console.error("Failed to save artifact edit:", error);
      }
    }, 500);
  };

  onCleanup(() => {
    if (saveDebounceTimer) {
      clearTimeout(saveDebounceTimer);
    }
  });

  const handleDiscardLocalEdits = async () => {
    try {
      if (saveDebounceTimer) {
        clearTimeout(saveDebounceTimer);
        saveDebounceTimer = null;
      }

      await clearArtifactEdit(props.artifact.blockId);
      setHasLocalEdits(false);
      setLoadedEditContent(null);
      setHasDirtyChanges(false); // Clear dirty state when discarding

      if (editorInstance() && props.artifact.metadata?.richText) {
        suppressDirtyTracking = true;
        editorInstance()!.commands.setContent(props.artifact.metadata.richText);
      }

      // Notify other components that edits have been cleared
      dispatchGlobalEvent("artifact-edit-cleared", { blockId: props.artifact.blockId });
    } catch (error) {
      console.error("Failed to discard local edits:", error);
    }
  };

  const handleDiscardUnsavedChanges = () => {
    // Clear pending save debounce
    if (saveDebounceTimer) {
      clearTimeout(saveDebounceTimer);
      saveDebounceTimer = null;
    }

    // Reload editor from last saved draft (or original if no draft)
    if (editorInstance()) {
      const contentToReload = loadedEditContent() || originalContent() || props.artifact.metadata?.richText;
      if (contentToReload) {
        suppressDirtyTracking = true;
        editorInstance()!.commands.setContent(contentToReload);
      }
    }

    // Clear dirty state
    setHasDirtyChanges(false);
  };

  onMount(() => {
    if (isServer) return;

    const loadEdit = async () => {
      setIsLoadingEdit(true);
      try {
        const editedArtifact = await loadArtifactEdit(props.artifact.blockId);
        if (editedArtifact) {
          setLoadedEditContent(editedArtifact.editedContent);
          setOriginalContent(editedArtifact.originalContent);
          setHasLocalEdits(true);
        }
      } catch (error) {
        console.error("Failed to load artifact edit:", error);
      } finally {
        setIsLoadingEdit(false);
      }
    };

    loadEdit();

    // Set up ResizeObserver to track panel width for responsive buttons
    const buttonContainer = document.querySelector(".artifact-button-container");
    if (buttonContainer) {
      let rafId: number | null = null;

      const resizeObserver = new ResizeObserver((entries) => {
        // Use requestAnimationFrame to batch updates and prevent ResizeObserver loop errors
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(() => {
          for (const entry of entries) {
            setPanelWidth(entry.contentRect.width);
          }
          rafId = null;
        });
      });

      resizeObserver.observe(buttonContainer);

      onCleanup(() => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        resizeObserver.disconnect();
      });
    }
  });

  const handleClose = () => {
    actions.setExtendedPaneMode(ExtendedPaneMode.HIDDEN);
  };

  const handleExitEditMode = async () => {
    if (saveDebounceTimer) {
      clearTimeout(saveDebounceTimer);
      saveDebounceTimer = null;

      if (editorInstance()) {
        try {
          const content = editorInstance()!.getJSON() as JSONContent;
          let original = originalContent();
          if (!original && props.artifact.metadata?.richText) {
            original = JSON.parse(JSON.stringify(props.artifact.metadata.richText)) as JSONContent;
          }
          await saveArtifactEdit(props.artifact.blockId, content, original, props.artifact.title, props.artifact.type);
          setLoadedEditContent(content);

          // Notify other components that edits have been saved
          dispatchGlobalEvent("artifact-edit-saved", { blockId: props.artifact.blockId });
        } catch (error) {
          console.error("Failed to save on exit:", error);
        }
      }
    }

    setHasDirtyChanges(false); // Clear dirty state after saving
    setEditMode(false);
  };

  // Create memo to determine rendering strategy based on processing state and user preference
  const shouldUseRichText = createMemo(() => {
    // Must have rich text data available
    const hasRichText = props.artifact?.metadata?.richText;

    // Must be done processing (stop message received)
    const isDoneProcessing = props.artifact && !props.artifact.isProcessing;

    return hasRichText && isDoneProcessing;
  });

  const [editorInstance, setEditorInstance] = createSignal<RichTextEditorInstance | undefined>(undefined);

  createEffect(
    on(
      () => props.artifact.blockId,
      () => {
        setEditorInstance(undefined);
      },
    ),
  );

  // Debug: Log toolbar visibility conditions
  createEffect(() => {
    const shouldShow = editMode() && !!editorInstance();
    console.log("[ArtifactViewer] Toolbar visibility", {
      editMode: editMode(),
      hasEditorInstance: !!editorInstance(),
      shouldShow,
    });
  });

  const [download, setDownload] = createSignal({
    downloading: false,
    err: false,
  });

  const [sharePdf, setSharePdf] = createSignal({
    sharing: false,
    copied: false,
    err: false,
  });

  const [showCopyToast, setShowCopyToast] = createSignal(false);

  const [copyRegular, setCopyRegular] = createSignal({
    copied: false,
  });

  const [copyMarkdown, setCopyMarkdown] = createSignal({
    copied: false,
  });

  const handleSaveToKB = async () => {
    if (isSavingToKB() || isLoadingEdit()) return;

    try {
      setIsSavingToKB(true);
      setSaveSuccess(false);

      let contentToExport: RichText.RichText;

      if (editorInstance()) {
        const editorJson = editorInstance()!.getJSON();
        contentToExport = proseMirrorToRichText(editorJson as unknown as PmDoc);
      } else {
        const editedArtifact = await loadArtifactEdit(props.artifact.blockId);
        if (editedArtifact?.editedContent) {
          contentToExport = proseMirrorToRichText(editedArtifact.editedContent as unknown as PmDoc);
        } else if (props.artifact.metadata?.richText) {
          contentToExport = props.artifact.metadata.richText;
        } else {
          throw new Error("No content available to save");
        }
      }

      let title = findTitle(contentToExport) || props.artifact.title;
      if (typeof title !== "string") {
        title = "Generated Document";
      }

      const exportResult = await useExportClient({ authToken: wire.auth.token }).exportRichText({
        richText: contentToExport,
        filename: title,
        format: ExportFormat.PDF,
      });

      if (exportResult.isErr()) {
        throw new Error(`Failed to convert to PDF: ${exportResult.error.message}`);
      }

      const pdfBuffer = toArrayBuffer(exportResult.value.fileData);
      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const authToken = wire.auth.token();
      if (!authToken) {
        throw new Error("Authentication required");
      }

      const response = await saveArtifactToKB(pdfBlob, title, authToken);

      console.log("Artifact saved to Knowledge Base successfully:", response);

      // Fetch the full asset details to update the store
      const request = GetAssetsRequestConfig.withBearerToken(authToken);
      const assetResult = await client.fetch(request, undefined, { assetIds: [response.assetId as AssetId] });

      if (assetResult.isOk() && assetResult.value.length > 0) {
        const asset = assetResult.value[0];
        const userId = wire.auth.user()?.id;

        if (userId) {
          // Direct store update - same pattern as file uploads
          wire.assets.set(asset.id as AssetId, { ...asset, userId });
        }

        // Open knowledge pane and highlight the newly saved asset
        // Use a small delay to ensure the asset is fully in the store
        setTimeout(() => {
          dispatchGlobalEvent("open-asset-in-knowledge-pane", { assetId: asset.id });
        }, 100);
      }

      // Fetch the system artifact label to update the label and relationship stores
      const labelResponse = await fetch(`${getBackendURL()}/labels/system/artifact`, {
        method: "GET",
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (labelResponse.ok) {
        const labelData = await labelResponse.json();
        // Update label store
        wire.labels.set(labelData.id, labelData);
        // Update relationship store to show label on the asset
        wire.rels.addLabelToAsset(response.assetId as AssetId, labelData.id);

        // Fetch and store the label association
        const associationsResponse = await fetch(
          `${getBackendURL()}/label-associations?resourceType=asset&resourceId=${response.assetId}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${authToken}` },
          },
        );

        if (associationsResponse.ok) {
          const associations = await associationsResponse.json();
          for (const assoc of associations) {
            wire.labels.onLabelAssociated(assoc);
          }
        }
      }

      // Dispatch event to refresh sync as fallback
      dispatchGlobalEvent("refresh-sync", undefined);

      setSaveSuccess(true);

      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Failed to save artifact to Knowledge Base:", error);
      let errorMessage = "Failed to save artifact";
      if (error instanceof ArtifactSaveError) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      alert(errorMessage);
    } finally {
      setIsSavingToKB(false);
    }
  };

  return (
    <div class={clsx("w-full h-full bg-panel relative", props.isActive ? "block" : "hidden", props.class)}>
      <Show when={shouldRenderContent()}>
        {/* Action buttons positioned at top right */}
        <div class="artifact-button-container absolute top-4 right-6 left-6 z-10 flex items-center gap-2 overflow-x-auto">
          {/* Add to All Knowledge button */}
          <StTooltip
            content={
              <p>
                {isSavingToKB() ? (
                  "Saving..."
                ) : saveSuccess() ? (
                  "Added!"
                ) : (
                  <>
                    Add this asset to <strong>All Knowledge</strong> library, labeled as{" "}
                    <strong>Generated by Storytell</strong>
                  </>
                )}
              </p>
            }
            portalProps={{ mount: props.mount }}
          >
            <StTooltipTrigger>
              <button
                type="button"
                onClick={handleSaveToKB}
                disabled={isSavingToKB()}
                class={clsx(
                  "inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-solid transition-colors cursor-pointer",
                  "bg-blue-50 dark:bg-blue-950/30 border-blue-500 text-blue-600 dark:text-blue-400",
                  "hover:bg-blue-100 dark:hover:bg-blue-950/50",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  saveSuccess() &&
                    "bg-green-50 dark:bg-green-950/30 border-green-500 text-green-600 dark:text-green-400",
                )}
              >
                <Show when={isSavingToKB()}>
                  <TbLoader2 class="size-4 animate-spin" />
                </Show>
                <Show when={!isSavingToKB() && saveSuccess()}>
                  <TbCheck class="size-4" />
                </Show>
                <Show when={!isSavingToKB() && !saveSuccess()}>
                  <TbPaperclip class="size-4" />
                </Show>
                <span class="text-sm font-medium">
                  {saveSuccess() ? (
                    "Added!"
                  ) : (
                    <>
                      Add to <strong>All Knowledge</strong>
                    </>
                  )}
                </span>
              </button>
            </StTooltipTrigger>
          </StTooltip>
          {/* Separator between save operations and copy actions */}
          <div class="h-6 w-px bg-muted-border/50" />

          {/* Copy button - always visible */}
          <StTooltip
            content={<p>{copyRegular().copied ? "Copied!" : "Copy to Clipboard"}</p>}
            portalProps={{ mount: props.mount }}
          >
            <StButton
              onClick={async () => {
                if (editMode() && editorInstance()) {
                  await copyToClipboard(editorInstance()!);
                } else {
                  // If there are saved edits, prioritize using those instead of rendered content
                  // (rendered content might show original even when edits exist)
                  if (hasLocalEdits() && loadedEditContent()) {
                    // Convert saved edit content to HTML for rich text copy
                    // Use editor instance if available (even when not in edit mode), or fall back
                    const htmlContent = await convertProseMirrorToHTML(loadedEditContent()!, editorInstance());
                    // convertProseMirrorToHTML now always returns HTML (uses editor or markdown->HTML conversion)
                    if (htmlContent) {
                      // Create HTML and plain text blobs for rich text copy
                      const htmlBlob = new Blob([htmlContent], { type: "text/html" });

                      // Convert HTML to plain text with formatting preserved
                      const tempDiv = document.createElement("div");
                      tempDiv.innerHTML = htmlContent;

                      // Replace <br> tags with newlines
                      const brElements = tempDiv.querySelectorAll("br");
                      brElements.forEach((br) => {
                        br.replaceWith(document.createTextNode("\n"));
                      });

                      // Extract text preserving list structure (reuse logic from copyToClipboard)
                      const extractTextFromNode = (node: Node): string => {
                        if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
                        if (node.nodeType !== Node.ELEMENT_NODE) return "";

                        const element = node as HTMLElement;
                        const tagName = element.tagName.toLowerCase();

                        if (tagName === "ul" || tagName === "ol") {
                          const items = Array.from(element.children)
                            .filter((el) => el.tagName.toLowerCase() === "li")
                            .map((li) => {
                              const listText = Array.from(li.childNodes)
                                .map((child) => extractTextFromNode(child))
                                .join("")
                                .trim();
                              const bullet = tagName === "ol" ? "" : "• ";
                              return `${bullet}${listText}`;
                            })
                            .filter((text) => text.trim().length > 0);
                          return items.join("\n");
                        }

                        if (["p", "div", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "pre"].includes(tagName)) {
                          const content = Array.from(element.childNodes)
                            .map((child) => extractTextFromNode(child))
                            .join("")
                            .trim();
                          return content;
                        }

                        return Array.from(element.childNodes)
                          .map((child) => extractTextFromNode(child))
                          .join("");
                      };

                      const textParts = Array.from(tempDiv.children)
                        .map((child) => {
                          return extractTextFromNode(child);
                        })
                        .filter((text) => text.trim().length > 0);

                      const plainTextContent = textParts.length > 0 ? textParts.join("\n") : tempDiv.textContent || "";
                      const plainTextBlob = new Blob([plainTextContent], { type: "text/plain" });

                      await navigator.clipboard.write([
                        new ClipboardItem({
                          "text/html": htmlBlob,
                          "text/plain": plainTextBlob,
                        }),
                      ]);
                    } else {
                      // Fallback to markdown if editor instance not available
                      const markdown = proseMirrorToMarkdown(loadedEditContent()!);
                      await copyMarkdownToClipboard(markdown);
                    }
                  } else {
                    // No saved edits - use rendered content if available
                    const content = contentRef();
                    if (content) {
                      await copyRichTextToClipboard(content);
                    } else {
                      // Fallback to markdown if no rendered content
                      const markdown = getArtifactMarkdown(props.artifact, wire);
                      await copyMarkdownToClipboard(markdown);
                    }
                  }
                }
                setCopyRegular({ copied: true });
                setTimeout(() => setCopyRegular({ copied: false }), 2000);
              }}
              as={StTooltipTrigger}
              icon={copyRegular().copied ? TbCheck : TbCopy}
              class="bg-tile hover:bg-tile-hover"
            >
              <Show when={panelWidth() >= 1000}>{copyRegular().copied ? "Copied!" : "Copy"}</Show>
            </StButton>
          </StTooltip>
          {/* Copy as Markdown button */}
          <StTooltip
            content={<p>{copyMarkdown().copied ? "Copied!" : "Copy as Markdown"}</p>}
            portalProps={{ mount: props.mount }}
          >
            <StButton
              onClick={async () => {
                let markdown: string;

                if (editMode() && editorInstance()) {
                  const json = editorInstance()!.getJSON();
                  markdown = proseMirrorToMarkdown(json);
                } else {
                  // Check if there are saved edits to use instead of original content
                  if (hasLocalEdits() && loadedEditContent()) {
                    markdown = proseMirrorToMarkdown(loadedEditContent()!);
                  } else {
                    markdown = getArtifactMarkdown(props.artifact, wire);
                  }
                }

                const success = await copyMarkdownToClipboard(markdown);
                if (success) {
                  setCopyMarkdown({ copied: true });
                  setTimeout(() => setCopyMarkdown({ copied: false }), 2000);
                }
              }}
              as={StTooltipTrigger}
              icon={copyMarkdown().copied ? TbCheck : TbMarkdown}
              class="bg-tile hover:bg-tile-hover"
            >
              <Show when={panelWidth() >= 1000}>{copyMarkdown().copied ? "Copied!" : "Copy as Markdown"}</Show>
            </StButton>
          </StTooltip>

          {/* Separator between copy actions and export operations */}
          <div class="h-6 w-px bg-muted-border/50" />

          <StTooltip
            content={<p>{download().err ? "Click to try again" : "Download PDF"}</p>}
            portalProps={{ mount: props.mount }}
          >
            <StButton
              loading={download().downloading}
              onClick={async () => {
                setDownload({ downloading: true, err: false });
                try {
                  await downloadArtifact(
                    props.artifact,
                    proseMirrorToRichText(
                      (editorInstance()?.getJSON() ?? props.artifact.metadata?.richText ?? {}) as unknown as PmDoc,
                    ),
                    wire.auth.token,
                  );
                  setDownload({ downloading: false, err: false });
                } catch (err) {
                  setDownload({ downloading: false, err: true });
                }
              }}
              as={StTooltipTrigger}
              icon={download().err ? TbAlertCircle : TbDownload}
              class="bg-tile hover:bg-tile-hover"
              classList={{
                "text-red-500": download().err,
              }}
            >
              <Show when={panelWidth() >= 1000}>{download().err ? "Download failed" : "Download PDF"}</Show>
            </StButton>
          </StTooltip>

          <Suspense>
            <StTooltip
              content={
                <p>
                  {sharePdf().err
                    ? "Click to try again"
                    : hasActualEdits() || hasDirtyChanges()
                      ? "Sharing disabled. Revert to Original to enable sharing."
                      : existingPublicAsset()
                        ? "Copy Shareable Link"
                        : "Share PDF"}
                </p>
              }
              portalProps={{ mount: props.mount }}
            >
              <StButton
                loading={sharePdf().sharing}
                disabled={hasActualEdits() || hasDirtyChanges()}
                onClick={async () => {
                  if (hasActualEdits() || hasDirtyChanges()) return;

                  setSharePdf({ sharing: true, copied: false, err: false });
                  try {
                    let publicAssetId: string;

                    // If there's an existing public asset, use it
                    if (existingPublicAsset()) {
                      publicAssetId = existingPublicAsset()!.id;
                    } else {
                      // Otherwise, create a new one
                      const markdown = getArtifactMarkdown(props.artifact, wire);
                      const request = CreatePublicAssetRequestConfig.withBearerToken(wire.auth.token());
                      const response = await client.fetch(request, undefined, {
                        markdown,
                        blockId: props.artifact.blockId,
                        chatId: params.chatId,
                      });

                      if (response.isErr()) {
                        throw response.error;
                      }

                      const publicAsset = response.value;

                      if (!publicAsset?.id) {
                        throw new Error("No asset ID in response");
                      }

                      publicAssetId = publicAsset.id;
                    }

                    const publicUrl = `${window.location.origin}/public-assets/${publicAssetId}`;
                    await navigator.clipboard.writeText(publicUrl);

                    setSharePdf({ sharing: false, copied: true, err: false });
                    setShowCopyToast(true);

                    // Reset copied state after 3 seconds
                    setTimeout(() => {
                      setSharePdf((prev) => ({ ...prev, copied: false }));
                    }, 3000);

                    // Hide toast after 3 seconds
                    setTimeout(() => {
                      setShowCopyToast(false);
                    }, 3000);
                  } catch (err) {
                    console.error("Failed to share PDF:", err);
                    setSharePdf({ sharing: false, copied: false, err: true });
                  }
                }}
                as={StTooltipTrigger}
                icon={sharePdf().err ? TbAlertCircle : TbShare}
                class="bg-tile hover:bg-tile-hover"
                classList={{
                  "text-red-500": sharePdf().err,
                  "opacity-50 cursor-not-allowed": hasActualEdits() || hasDirtyChanges(),
                }}
              >
                <Show when={panelWidth() >= 1000}>
                  {sharePdf().err
                    ? "Share failed"
                    : sharePdf().copied
                      ? "Link copied to clipboard"
                      : hasActualEdits() || hasDirtyChanges()
                        ? "Share Original PDF"
                        : existingPublicAsset()
                          ? "Copy Shareable Link"
                          : "Share PDF"}
                </Show>
              </StButton>
            </StTooltip>
          </Suspense>

          {/* Close button - pushed to far right */}
          <div class="ml-auto flex items-center">
            <StTooltip content={<p>Close Editor</p>} portalProps={{ mount: props.mount }}>
              <StButton
                onClick={handleClose}
                class="bg-tile hover:bg-tile-hover"
                noColor
                noBorder
                icon={TbX}
                aria-label="Close Editor"
                as={StTooltipTrigger}
              />
            </StTooltip>
          </div>
        </div>

        <Show when={!editMode()}>
          <div class="absolute top-[4.5rem] right-6 left-6 z-20 flex items-center justify-start gap-1.5">
            <Show when={hasActualEdits()}>
              <StTooltip content={<p>Revert to Original</p>} portalProps={{ mount: props.mount }}>
                <StButton
                  onClick={handleDiscardLocalEdits}
                  as={StTooltipTrigger}
                  icon={TbArrowBackUp}
                  class="bg-tile hover:bg-tile-hover"
                >
                  <Show when={panelWidth() >= 1000}>Revert to Original</Show>
                </StButton>
              </StTooltip>
            </Show>
            <StTooltip content={<p>Open Edit Mode</p>} portalProps={{ mount: props.mount }}>
              <StButton
                icon={TbPencil}
                class="bg-tile hover:bg-tile-hover"
                onClick={() => setEditMode(true)}
                as={StTooltipTrigger}
              >
                <Show when={panelWidth() >= 1000}>Open Edit Mode</Show>
              </StButton>
            </StTooltip>
            <Show when={hasActualEdits()}>
              <span class="text-xs px-2 py-1 rounded-full font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                Modified
              </span>
            </Show>
          </div>
        </Show>

        {/* Second row - Editing banner with Revert and Exit buttons */}
        <Show when={editMode()}>
          <div class="absolute top-[4.5rem] right-6 left-6 z-20">
            <div class="bg-blue-100 dark:bg-blue-900/30 rounded-lg px-4 py-2 flex items-center justify-between">
              <div class="flex items-center gap-2">
                {/* Revert to Original button - show when there are any modifications (saved or unsaved) */}
                <Show when={hasDirtyChanges() || hasActualEdits()}>
                  <StTooltip content={<p>Revert to Original</p>} portalProps={{ mount: props.mount }}>
                    <StButton
                      onClick={handleDiscardLocalEdits}
                      as={StTooltipTrigger}
                      icon={TbArrowBackUp}
                      class="bg-tile hover:bg-tile-hover"
                    >
                      <Show when={panelWidth() >= 1000}>Revert to Original</Show>
                    </StButton>
                  </StTooltip>
                </Show>
                <span class="text-sm font-medium text-blue-900 dark:text-blue-200">Editing</span>
              </div>
              <div class="flex items-center gap-2">
                {/* Learn about the Editor link */}
                <a
                  href="https://go.storytell.ai/learn-editor"
                  target="_blank"
                  rel="noreferrer"
                  class="inline-flex items-center gap-1 text-xs text-blue-700 dark:text-blue-300 hover:underline mr-auto"
                  aria-label="Learn about the Editor"
                >
                  <TbInfoCircle class="size-3" />
                  Learn about the Editor
                </a>
                {/* Discard unsaved changes button - only visible when editing with unsaved changes */}
                <Show when={hasDirtyChanges()}>
                  <StTooltip content={<p>Exit and Discard Unsaved Changes</p>} portalProps={{ mount: props.mount }}>
                    <StButton
                      onClick={() => {
                        handleDiscardUnsavedChanges();
                        setEditMode(false);
                      }}
                      as={StTooltipTrigger}
                      icon={TbX}
                      class="bg-tile hover:bg-tile-hover"
                    >
                      <Show when={panelWidth() >= 1000}>Exit and Discard Unsaved Changes</Show>
                    </StButton>
                  </StTooltip>
                </Show>
                {/* Exit button - always visible in edit mode */}
                <StTooltip
                  content={<p>{hasDirtyChanges() ? "Save and Exit" : "Exit"}</p>}
                  portalProps={{ mount: props.mount }}
                >
                  <div class="relative">
                    <StButton
                      icon={hasDirtyChanges() ? TbDeviceFloppy : TbX}
                      class="bg-tile hover:bg-tile-hover"
                      onClick={handleExitEditMode}
                      as={StTooltipTrigger}
                    >
                      <Show when={panelWidth() >= 1000}>{hasDirtyChanges() ? "Save and Exit" : "Exit"}</Show>
                    </StButton>
                    <Show when={hasDirtyChanges()}>
                      <div class="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-500 rounded-full border-2 border-panel" />
                    </Show>
                  </div>
                </StTooltip>
              </div>
            </div>
          </div>
          {/* Formatting toolbar - below Edit Mode banner */}
          <Show when={editMode() && editorInstance()}>
            <div class="absolute top-[7.5rem] right-6 left-6 z-20 flex items-center gap-2 py-2.5">
              {/* Bold */}
              <StTooltip content={<p>Bold</p>} portalProps={{ mount: props.mount }}>
                <StTooltipTrigger
                  as="button"
                  type="button"
                  onClick={() => editorInstance()?.chain().focus().toggleBold().run()}
                  data-active={editorState().bold || undefined}
                  class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-8 grid place-content-center rounded data-[active]:font-bold data-[active]:bg-background"
                >
                  B
                </StTooltipTrigger>
              </StTooltip>
              {/* Italic */}
              <StTooltip content={<p>Italic</p>} portalProps={{ mount: props.mount }}>
                <StTooltipTrigger
                  as="button"
                  type="button"
                  data-active={editorState().italic || undefined}
                  onClick={() => editorInstance()?.chain().focus().toggleItalic().run()}
                  class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-8 grid place-content-center rounded data-[active]:font-bold data-[active]:bg-background italic"
                >
                  I
                </StTooltipTrigger>
              </StTooltip>
              {/* Strikethrough */}
              <StTooltip content={<p>Strikethrough</p>} portalProps={{ mount: props.mount }}>
                <StTooltipTrigger
                  as="button"
                  type="button"
                  data-active={editorState().strike || undefined}
                  onClick={() => editorInstance()?.chain().focus().toggleStrike().run()}
                  class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-8 grid place-content-center rounded data-[active]:font-bold data-[active]:bg-background"
                >
                  <span class="line-through">S</span>
                </StTooltipTrigger>
              </StTooltip>
              {/* Separator */}
              <div class="h-4 w-[1px] bg-inset" />
              {/* Bullet List */}
              <StTooltip content={<p>Bullet List</p>} portalProps={{ mount: props.mount }}>
                <StTooltipTrigger
                  as="button"
                  type="button"
                  data-active={editorState().bulletList || undefined}
                  onClick={() => editorInstance()?.chain().focus().toggleBulletList().run()}
                  class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-8 grid place-content-center rounded data-[active]:bg-background"
                  title="Bullet list"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="size-4"
                  >
                    <path d="M9 6h11M9 12h11M9 18h11" />
                    <circle cx="3" cy="6" r="1" fill="currentColor" />
                    <circle cx="3" cy="12" r="1" fill="currentColor" />
                    <circle cx="3" cy="18" r="1" fill="currentColor" />
                  </svg>
                </StTooltipTrigger>
              </StTooltip>
              {/* Ordered List */}
              <StTooltip content={<p>Numbered List</p>} portalProps={{ mount: props.mount }}>
                <StTooltipTrigger
                  as="button"
                  type="button"
                  data-active={editorState().orderedList || undefined}
                  onClick={() => editorInstance()?.chain().focus().toggleOrderedList().run()}
                  class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-8 grid place-content-center rounded data-[active]:bg-background"
                  title="Numbered list"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="size-4"
                  >
                    <path d="M10 6h11M10 12h11M10 18h11" />
                    {/* Number 1 - simple vertical line */}
                    <path d="M4 4v6M3 5h2" stroke-width="2" />
                    {/* Number 2 - two-part curved shape */}
                    <path
                      d="M3 11c1 0 1.5 0.5 1.5 1.5s-0.5 1.5-1.5 1.5M3 13.5c1 0 1.5 0.5 1.5 1.5s-0.5 1.5-1.5 1.5"
                      stroke-width="2"
                    />
                    {/* Number 3 - two curved parts */}
                    <path
                      d="M3 18c1 0 1.5 0.5 1.5 1.5s-0.5 1.5-1.5 1.5M3 18.5c1 0 1.5 0.5 1.5 1.5s-0.5 1.5-1.5 1.5"
                      stroke-width="2"
                    />
                  </svg>
                </StTooltipTrigger>
              </StTooltip>
              {/* Separator */}
              <div class="h-4 w-[1px] bg-inset" />
              {/* Heading 1 */}
              <StTooltip content={<p>Heading 1</p>} portalProps={{ mount: props.mount }}>
                <StTooltipTrigger
                  as="button"
                  type="button"
                  data-active={editorState().heading1 || undefined}
                  onClick={() => editorInstance()?.chain().focus().setHeading({ level: 1 }).run()}
                  class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-10 grid place-content-center rounded data-[active]:font-bold data-[active]:bg-background text-xs"
                  title="Heading 1"
                >
                  H1
                </StTooltipTrigger>
              </StTooltip>
              {/* Heading 2 */}
              <StTooltip content={<p>Heading 2</p>} portalProps={{ mount: props.mount }}>
                <StTooltipTrigger
                  as="button"
                  type="button"
                  data-active={editorState().heading2 || undefined}
                  onClick={() => editorInstance()?.chain().focus().setHeading({ level: 2 }).run()}
                  class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-10 grid place-content-center rounded data-[active]:font-bold data-[active]:bg-background text-xs"
                  title="Heading 2"
                >
                  H2
                </StTooltipTrigger>
              </StTooltip>
              {/* Heading 3 */}
              <StTooltip content={<p>Heading 3</p>} portalProps={{ mount: props.mount }}>
                <StTooltipTrigger
                  as="button"
                  type="button"
                  data-active={editorState().heading3 || undefined}
                  onClick={() => editorInstance()?.chain().focus().setHeading({ level: 3 }).run()}
                  class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-10 grid place-content-center rounded data-[active]:font-bold data-[active]:bg-background text-xs"
                  title="Heading 3"
                >
                  H3
                </StTooltipTrigger>
              </StTooltip>
              {/* Regular Text / Paragraph */}
              <StTooltip content={<p>Regular Text</p>} portalProps={{ mount: props.mount }}>
                <StTooltipTrigger
                  as="button"
                  type="button"
                  data-active={editorState().paragraph || undefined}
                  onClick={() => editorInstance()?.chain().focus().setParagraph().run()}
                  class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-8 grid place-content-center rounded data-[active]:bg-background text-xs"
                  title="Regular Text"
                >
                  T
                </StTooltipTrigger>
              </StTooltip>
              {/* Separator */}
              <div class="h-4 w-[1px] bg-inset" />
              {/* Inline Code */}
              <StTooltip content={<p>Inline Code</p>} portalProps={{ mount: props.mount }}>
                <StTooltipTrigger
                  as="button"
                  type="button"
                  data-active={editorState().code || undefined}
                  onClick={() => editorInstance()?.chain().focus().toggleCode().run()}
                  class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-8 grid place-content-center rounded data-[active]:font-bold data-[active]:bg-background"
                  title="Inline Code"
                >
                  <TbCode class="size-4" />
                </StTooltipTrigger>
              </StTooltip>
              {/* Code Block */}
              <StTooltip content={<p>Code Block</p>} portalProps={{ mount: props.mount }}>
                <StTooltipTrigger
                  as="button"
                  type="button"
                  data-active={editorState().codeBlock || undefined}
                  onClick={() => editorInstance()?.chain().focus().toggleCodeBlock().run()}
                  class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-8 grid place-content-center rounded data-[active]:font-bold data-[active]:bg-background"
                  title="Code Block"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="size-4"
                  >
                    {/* Represent multiple lines of code */}
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                    <path d="M8 8h8M8 12h6M8 16h4" />
                  </svg>
                </StTooltipTrigger>
              </StTooltip>
            </div>
          </Show>
        </Show>

        {/* Artifact content - full height scrollable area */}
        <div
          class="h-full overflow-auto relative"
          classList={{
            "pt-[10.5rem]": editMode(), // Space for top toolbar + Editing banner + formatting toolbar
            "pt-[6rem]": !editMode(), // Space for top toolbar + Edit button row when not in edit mode
          }}
        >
          <UnifiedArtifactRenderer
            artifact={props.artifact}
            shouldUseRichText={shouldUseRichText}
            editMode={editMode}
            onEditorReady={(editor) => {
              console.log("[ArtifactViewer] onEditorReady called", { editMode: editMode(), hasEditor: !!editor });
              setEditorInstance(editor);
            }}
            onContentRef={setContentRef}
            loadedEditContent={loadedEditContent}
            isLoadingEdit={isLoadingEdit()}
            onContentChange={(content) => {
              if (suppressDirtyTracking) {
                suppressDirtyTracking = false;
                return;
              }

              setHasDirtyChanges(true); // Set dirty flag immediately on first keystroke
              debouncedSaveToIndexedDB(content);
            }}
            editedContentAsText={editedContentAsText}
            onEditorStateChange={setEditorState}
            onBeforeContentSet={() => {
              suppressDirtyTracking = true;
            }}
          />
        </div>
      </Show>

      {/* Toast notification for link copied */}
      <Show when={showCopyToast()}>
        <div class="fixed top-4 right-4 z-50 bg-tile border border-primary-border shadow-xl rounded-lg px-4 py-3 flex items-center gap-2 animate-in slide-in-from-right fade-in duration-300">
          <TbCheck class="size-5 text-green-500" />
          <span class="text-sm font-medium">Link copied to clipboard</span>
        </div>
      </Show>
    </div>
  );
};

// Streaming markdown component optimized for artifact content
const StreamingArtifactMarkdown: Component<{
  artifact: ArtifactState;
  webSearchResults?: WebSearchResultsCollection;
  kbSearchResults?: KBSearchResultsCollection;
  onContentRef?: (el: HTMLDivElement) => void;
  contentOverride?: string | null;
}> = (props) => {
  const wire = useWire();
  let lastId = props.artifact.blockId;
  const markdownStreamer = createMarkdownStreamerFromDeltas(
    () => {
      if (props.contentOverride) {
        return [props.contentOverride];
      }
      return wire.chat.getBlock(props.artifact.blockId as StreamingBlockId)?.deltas?.map((d) => d.text) ?? [];
    },
    () => {
      if (lastId === props.artifact.blockId) {
        return false;
      }
      lastId = props.artifact.blockId;
      return true;
    },
  );

  const [contentRef, setContentRef] = createSignal<HTMLDivElement>();

  return (
    <>
      <div
        ref={(el) => {
          setContentRef(el);
          if (props.onContentRef) {
            props.onContentRef(el);
          }
        }}
      >
        <SolidStreamingMarkdown
          doc={markdownStreamer.doc}
          options={{
            components: {
              htmlInline: (componentProps) => {
                const literal = () => componentProps.node.literal;
                const isCitation = () => literal()?.startsWith("<cite>") && literal()?.endsWith("</cite>");
                const citationValue = () => {
                  const lit = literal();
                  if (!lit) return "";
                  return lit.replace("<cite>", "").replace("</cite>", "");
                };

                return (
                  <Show when={isCitation()} fallback={null}>
                    <UnifiedCitationRenderer
                      node={{ attrs: { value: citationValue() } }}
                      selected={false}
                      webSearchResults={props.webSearchResults}
                      kbSearchResults={props.kbSearchResults}
                    />
                  </Show>
                );
              },
            },
            classes: {
              document: "**:animate-fade-in wrap-break-word whitespace-break-spaces px-4",
              heading: (node) => {
                switch (node.level) {
                  case 1:
                    return "font-medium text-3xl mb-3 mt-4";
                  case 2:
                    return "font-medium text-2xl mb-3 mt-4";
                  case 3:
                    return "font-medium text-xl mb-3 mt-4";
                  case 4:
                    return "font-medium text-lg mb-3 mt-4";
                  case 5:
                    return "font-medium text-base mb-3 mt-4";
                  case 6:
                    return "font-medium text-sm mb-3 mt-4";
                  default:
                    return "";
                }
              },
              // paragraph: "mb-2",
              list: (node) => {
                switch (node.listData?.type) {
                  case "ordered":
                    return "list-decimal pl-4";
                  case "bullet":
                    return "list-disc pl-4";
                  default:
                    return "";
                }
              },
              paragraph: "mb-4",
              // text: "animate-fade-in",
              item: "mb-2",
              code: "bg-black/10 dark:bg-white/10 px-0.5 py-0.5 rounded text-sm",
              blockQuote: "border-l-2 border-indigo-100 dark:border-indigo-700 pl-2 py-1 my-1",
              tableWrapper: "overflow-x-auto w-full max-w-full rounded-lg border border-muted-border",
              table: "",
              tableRow: "border-b border-muted-border [&:last-child]:border-b-0",
              tableCell: "px-2 py-2 border-r border-muted-border [&:last-child]:border-r-0",
              tableHead: "bg-panel text-sm font-semibold [&_td]:whitespace-nowrap",
              tableBody: "bg-tile",
              link: "text-brand underline",
            },
          }}
        />
      </div>
      <TextSelectionHandler ref={contentRef} isReadOnly={false} />
    </>
  );
};

// Unified renderer that handles all artifact types
const UnifiedArtifactRenderer: Component<{
  artifact: ArtifactState;
  shouldUseRichText: () => boolean;
  editMode: () => boolean;
  onEditorReady: (editor: RichTextEditorInstance) => void;
  onContentRef: (el: HTMLDivElement) => void;
  loadedEditContent: (() => JSONContent | null) | null;
  isLoadingEdit?: boolean;
  onContentChange?: (content: JSONContent) => void;
  editedContentAsText?: () => string | null;
  onBeforeContentSet?: () => void;
  onEditorStateChange?: (state: {
    bold: boolean;
    italic: boolean;
    strike: boolean;
    code: boolean;
    codeBlock: boolean;
    bulletList: boolean;
    orderedList: boolean;
    heading1: boolean;
    heading2: boolean;
    heading3: boolean;
    paragraph: boolean;
  }) => void;
}> = (props) => {
  const wire = useWire();
  const { state } = useAppUI();
  let scrollableStartPoint: HTMLDivElement | undefined;

  const [transitionToEditor, setTransitionToEditor] = createSignal(false);

  let currentEditor: RichTextEditorInstance | undefined;
  let contentUpdateHandler: ((args: { editor: RichTextEditorInstance }) => void) | undefined;
  let selectionUpdateHandler: ((args: { editor: RichTextEditorInstance }) => void) | undefined;

  onCleanup(() => {
    if (currentEditor) {
      if (contentUpdateHandler) {
        currentEditor.off("update", contentUpdateHandler);
      }
      if (selectionUpdateHandler) {
        currentEditor.off("selectionUpdate", selectionUpdateHandler);
      }
    }
  });

  createEffect(() => {
    const content = props.loadedEditContent?.();
    if (currentEditor && content) {
      const currentContent = currentEditor.getJSON();
      if (JSON.stringify(currentContent) !== JSON.stringify(content)) {
        props.onBeforeContentSet?.();
        currentEditor.commands.setContent(content);
      }
    }
  });

  const shouldUseRichText = createMemo(() => props.shouldUseRichText() && state.useRichTextReplacement);

  // Build web search results collection from all webSearchResults objects in the same message as the artifact
  const webSearchResults = createMemo<WebSearchResultsCollection | undefined>(() => {
    const block = wire.chat.getBlock(props.artifact.blockId as StreamingBlockId);
    if (!block) return undefined;

    const operation = wire.stream.operation.get(block.operationId);
    if (!operation) return undefined;

    const message = wire.stream.message.get(operation.messageId);
    if (!message) return undefined;

    const allWebSearchResults: ChatMessages.WebSearchResult[] = [];

    // Look through all operations in this message
    for (const operationId of message.operations ?? []) {
      const messageOp = wire.stream.operation.get(operationId);
      if (!messageOp) continue;

      // Look through all blocks in this operation
      for (const blockId of messageOp.blocks ?? []) {
        const opBlock = wire.stream.block.get(blockId);
        if (opBlock?.webSearchResults) {
          // Collect individual webSearchResults objects from each block
          allWebSearchResults.push(opBlock.webSearchResults);
        }
      }
    }

    // Build collection for efficient lookup by tool ID
    return allWebSearchResults.length > 0 ? buildWebSearchCollection(allWebSearchResults) : undefined;
  });

  // Build KB search results collection from all searchResults objects in the same message as the artifact
  const kbSearchResults = createMemo<KBSearchResultsCollection | undefined>(() => {
    const block = wire.chat.getBlock(props.artifact.blockId as StreamingBlockId);
    if (!block) return undefined;

    const operation = wire.stream.operation.get(block.operationId);
    if (!operation) return undefined;

    const message = wire.stream.message.get(operation.messageId);
    if (!message) return undefined;

    const collections: KBSearchResultsCollection[] = [];

    for (const operationId of message.operations ?? []) {
      const messageOp = wire.stream.operation.get(operationId);
      if (!messageOp) continue;
      if (messageOp.activity !== "user_kb_search_v1" && messageOp.activity !== "kb_search") continue;

      for (const blockId of messageOp.blocks ?? []) {
        const opBlock = wire.stream.block.get(blockId);
        if (opBlock?.searchResults) {
          const collection = buildKBCitationIndex(opBlock.searchResults);
          collections.push(collection);
        }
      }
    }

    if (collections.length === 0) return undefined;

    const merged: KBSearchResultsCollection = {
      byToolId: new Map(),
      byChunkId: new Map(),
    };

    for (const collection of collections) {
      for (const [toolId, results] of collection.byToolId) {
        merged.byToolId.set(toolId, results);
      }
      for (const [chunkId, chunkData] of collection.byChunkId) {
        merged.byChunkId.set(chunkId, chunkData);
      }
    }

    return merged;
  });

  const getScrollableParent = () => {
    let current = scrollableStartPoint;
    while (current) {
      if (current.scrollTop > 0) {
        return current;
      }
      current = current.parentElement as HTMLDivElement;
    }
    return scrollableStartPoint;
  };

  return (
    <div class="h-full p-4" ref={scrollableStartPoint}>
      <div class="h-full overflow-auto relative pb-20">
        <div class="">
          {/* Left spacer - grows/shrinks more aggressively with panel width */}
          <div class="flex-[2] min-w-2 max-w-100" />

          {/* Content column - maintains readable width */}
          <div class="max-w-[90ch] w-full">
            {/* Always show streaming markdown when rich text is disabled or during transition */}
            <Show when={!shouldUseRichText() || !transitionToEditor()}>
              <StreamingArtifactMarkdown
                artifact={props.artifact}
                webSearchResults={webSearchResults()}
                kbSearchResults={kbSearchResults()}
                onContentRef={props.onContentRef}
                contentOverride={props.editedContentAsText?.()}
              />
            </Show>

            {/* Only show rich text renderer when enabled and available */}
            <Show when={shouldUseRichText() && props.artifact.metadata?.richText && !props.isLoadingEdit}>
              <div classList={{ "absolute inset-0": !transitionToEditor() }}>
                <RichTextEditor
                  editable={props.editMode()}
                  onEditorReady={(editor) => {
                    // Clean up old handlers
                    if (currentEditor) {
                      if (contentUpdateHandler) {
                        currentEditor.off("update", contentUpdateHandler);
                      }
                      if (selectionUpdateHandler) {
                        currentEditor.off("selectionUpdate", selectionUpdateHandler);
                      }
                    }

                    currentEditor = editor;
                    props.onEditorReady(editor);

                    const content = props.loadedEditContent?.();

                    // Set up content change handler BEFORE setting content, so baseline is established correctly
                    if (props.editMode() && props.onContentChange) {
                      // Initialize with content that will be set (if any), or current editor content
                      const initialContent = content || (editor.getJSON() as JSONContent);
                      let lastContent: JSONContent | null = initialContent;

                      contentUpdateHandler = ({ editor: updatedEditor }) => {
                        const currentContent = updatedEditor.getJSON();
                        const contentString = JSON.stringify(currentContent);
                        const lastContentString = lastContent ? JSON.stringify(lastContent) : null;

                        // Only call onContentChange if content actually changed
                        if (contentString !== lastContentString) {
                          lastContent = currentContent as JSONContent;
                          props.onContentChange?.(currentContent as JSONContent);
                        } else {
                          // Content is the same but update event fired - update baseline without triggering change
                          lastContent = currentContent as JSONContent;
                        }
                      };

                      editor.on("update", contentUpdateHandler);

                      selectionUpdateHandler = ({ editor: updatedEditor }) => {
                        // Update editor state for toolbar buttons on selection changes
                        props.onEditorStateChange?.({
                          bold: updatedEditor.isActive("bold"),
                          italic: updatedEditor.isActive("italic"),
                          strike: updatedEditor.isActive("strike"),
                          code: updatedEditor.isActive("code"),
                          codeBlock: updatedEditor.isActive("codeBlock"),
                          bulletList: updatedEditor.isActive("bulletList"),
                          orderedList: updatedEditor.isActive("orderedList"),
                          heading1: updatedEditor.isActive("heading", { level: 1 }),
                          heading2: updatedEditor.isActive("heading", { level: 2 }),
                          heading3: updatedEditor.isActive("heading", { level: 3 }),
                          paragraph: updatedEditor.isActive("paragraph"),
                        });
                      };

                      editor.on("selectionUpdate", selectionUpdateHandler);
                    }

                    if (content) {
                      props.onBeforeContentSet?.();
                      editor.commands.setContent(content);
                    }

                    // Initialize editor state for toolbar
                    if (props.editMode()) {
                      props.onEditorStateChange?.({
                        bold: editor.isActive("bold"),
                        italic: editor.isActive("italic"),
                        strike: editor.isActive("strike"),
                        code: editor.isActive("code"),
                        codeBlock: editor.isActive("codeBlock"),
                        bulletList: editor.isActive("bulletList"),
                        orderedList: editor.isActive("orderedList"),
                        heading1: editor.isActive("heading", { level: 1 }),
                        heading2: editor.isActive("heading", { level: 2 }),
                        heading3: editor.isActive("heading", { level: 3 }),
                        paragraph: editor.isActive("paragraph"),
                      });
                    }

                    const parent = getScrollableParent();
                    const scroll = parent?.scrollTop;
                    setTransitionToEditor(true);
                    requestAnimationFrame(() => {
                      parent?.scrollTo({ top: scroll, behavior: "instant" });
                    });
                  }}
                  doc={props.artifact.metadata?.richText!}
                  onQuoteInPromptBar={(data) => {
                    dispatchGlobalEvent("quote-in-prompt-bar", data);
                  }}
                  citationRenderer={(citationProps) => null}
                />
              </div>
            </Show>
          </div>

          {/* Right spacer - less aggressive scaling to provide padding */}
          <div class="flex-[1] min-w-2 max-w-100" />
        </div>
      </div>
    </div>
  );
};

// Utility functions
const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

// Helper to convert ProseMirror JSON to HTML for clipboard
// Uses editor instance if available, otherwise converts via markdown -> HTML
const convertProseMirrorToHTML = async (
  jsonContent: JSONContent,
  editor?: RichTextEditorInstance | null,
): Promise<string> => {
  if (isServer) return "";

  // If editor instance is available, use it temporarily
  if (editor) {
    try {
      const currentContent = editor.getJSON();
      editor.commands.setContent(jsonContent);
      // Wait a tick for content to be set
      await new Promise((resolve) => setTimeout(resolve, 0));
      const html = editor.getHTML();
      // Restore original content
      editor.commands.setContent(currentContent);
      return html;
    } catch (error) {
      console.error("Failed to convert ProseMirror to HTML using editor:", error);
    }
  }

  // Fallback: Convert to markdown, then markdown to HTML
  // This ensures we always get HTML even without an editor instance
  try {
    const markdown = proseMirrorToMarkdown(jsonContent);
    // Convert markdown to HTML using a simple approach
    // Basic markdown -> HTML conversion
    const html = markdown
      // Headers
      .replace(/^### (.*$)/gim, "<h3>$1</h3>")
      .replace(/^## (.*$)/gim, "<h2>$1</h2>")
      .replace(/^# (.*$)/gim, "<h1>$1</h1>")
      // Bold
      .replace(/\*\*(.*?)\*\*/gim, "<strong>$1</strong>")
      // Italic
      .replace(/\*(.*?)\*/gim, "<em>$1</em>")
      // Code blocks
      .replace(/```[\s\S]*?```/gim, (match) => {
        const code = match.replace(/```/g, "");
        return `<pre><code>${code}</code></pre>`;
      })
      // Inline code
      .replace(/`(.*?)`/gim, "<code>$1</code>")
      // Lists
      .replace(/^\- (.*$)/gim, "<li>$1</li>")
      .replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>")
      // Paragraphs (lines that aren't headers or lists)
      .replace(/^(?!<[hul])(.+)$/gim, "<p>$1</p>")
      // Line breaks
      .replace(/\n/g, "<br>");

    return `<div>${html}</div>`;
  } catch (error) {
    console.error("Failed to convert ProseMirror to HTML via markdown:", error);
    return "";
  }
};

const copyToClipboard = async (editor: RichTextEditorInstance) => {
  if (isServer) return;
  // 1. Get the content as HTML from the Tiptap editor
  const htmlContent = editor.getHTML();

  // 2. Create a Blob for the HTML content
  //    The 'text/html' MIME type is crucial for rich text copy
  const htmlBlob = new Blob([htmlContent], { type: "text/html" });

  // 3. Create a ClipboardItem.
  //    Convert HTML to plain text while preserving line breaks and list structure
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;

  // Replace <br> tags with newlines first
  const brElements = tempDiv.querySelectorAll("br");
  brElements.forEach((br) => {
    br.replaceWith(document.createTextNode("\n"));
  });

  // Function to extract text from a node, preserving list structure
  const extractTextFromNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || "";
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return "";
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    // Handle lists - extract all list items with proper formatting
    if (tagName === "ul" || tagName === "ol") {
      const items = Array.from(element.children)
        .filter((el) => el.tagName.toLowerCase() === "li")
        .map((li) => {
          const listText = Array.from(li.childNodes)
            .map((child) => extractTextFromNode(child))
            .join("")
            .trim();
          const bullet = tagName === "ol" ? "" : "• ";
          return `${bullet}${listText}`;
        })
        .filter((text) => text.trim().length > 0);
      return items.join("\n");
    }

    // Handle block elements
    if (["p", "div", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "pre"].includes(tagName)) {
      const content = Array.from(element.childNodes)
        .map((child) => extractTextFromNode(child))
        .join("")
        .trim();
      return content;
    }

    // For other elements, recursively extract text
    return Array.from(element.childNodes)
      .map((child) => extractTextFromNode(child))
      .join("");
  };

  // Extract text from all top-level children
  const textParts = Array.from(tempDiv.children)
    .map((child) => {
      return extractTextFromNode(child);
    })
    .filter((text) => text.trim().length > 0);

  const plainTextContent = textParts.length > 0 ? textParts.join("\n") : tempDiv.textContent || "";
  const plainTextBlob = new Blob([plainTextContent], { type: "text/plain" });

  const clipboardItem = new ClipboardItem({
    "text/html": htmlBlob,
    "text/plain": plainTextBlob,
  });

  try {
    // 4. Write the ClipboardItem to the clipboard
    await navigator.clipboard.write([clipboardItem]);
    console.log("Editor content (rich text) copied to clipboard successfully!");
  } catch (err) {
    console.error("Failed to copy editor content:", err);
    // Fallback for older browsers or if Clipboard API fails for some reason
    // You might try document.execCommand('copy') here, but it's less reliable for rich text
    // and generally requires a selection or focus.
    alert("Failed to copy content. Please try copying manually or check console for details.");
  }
};

const getArtifactMarkdown = (artifact: ArtifactState, wire: ReturnType<typeof useWire>): string => {
  const block = wire.chat.getBlock(artifact.blockId as StreamingBlockId);
  if (!block) {
    throw new Error("Block not found");
  }

  // Get all deltas and join them to form the markdown content
  const markdown = block.deltas?.map((d) => d.text).join("") ?? "";
  return markdown;
};

const downloadArtifact = async (
  artifact: ArtifactState,
  richText: RichText.RichText,
  authToken: () => string | undefined,
) => {
  if (isServer) return;
  let title = findTitle(richText) || artifact.title;
  if (typeof title !== "string") {
    title = "Generated Document";
  }

  const result = await useExportClient({ authToken }).exportRichText({
    richText: richText,
    filename: title,
    format: ExportFormat.PDF,
  });

  if (result.isErr()) {
    console.error("Failed to download artifact:", result.error);
    throw result.error;
  }

  try {
    const arrayBuffer = toArrayBuffer(result.value.fileData);
    const blob = new Blob([arrayBuffer], { type: result.value.contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/[^a-z0-9\s'":-]/gi, "_")}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Failed to download artifact:", err);
  }
};
